import React, { useState, useCallback } from 'react';
import { View, Pressable, Platform, useWindowDimensions } from 'react-native';
import HorizontalCarousel from '@components/Global/Carousel/HorizontalCarousel';
import { Entypo, FontAwesome } from '@expo/vector-icons';
import { ADD_BASKET_ITEM, REMOVE_BASKET_ITEM } from '@reduxLocal/actions/types';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useDispatch, useSelector } from 'react-redux';
import styles from '@stylesheet';
import * as Animatable from 'react-native-animatable';
import { H4, H5, Paragraph, SizableText, XStack } from 'tamagui';
import ErrorBoundary from '@utils/ErrorBoundary';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

const ProductItem = ({ item, index }) => {
  const basketItems = useSelector((state: DefaultRootState) => state.store.basketItems);
  const dispatch = useDispatch();
  const theme = themeSelector();
  const { width } = useWindowDimensions();
  const isSmallScreen = width < 600;
  const showCompactMode = isSmallScreen;

  const getDescription = () => {
    //Find any key that contains the word description
    const descriptionKey = Object.keys(item.data).filter(key => /description|desc|product_description|product_desc/i.test(key));
    if (descriptionKey.length > 0) {
      return item.data[descriptionKey[0]];
    }
    else {
      return item.data.product_description || item.data.description || item.description || item.data.product_description || item.data.description || item.data.product_description || item.data.description;
    }
  }

  const _handleAddBasket = useCallback((item) => {
    dispatch({ type: ADD_BASKET_ITEM, payload: item })
  }, []);

  const _removeItemFromBasket = () => {
    dispatch({ type: REMOVE_BASKET_ITEM, payload: item })
  }

  const checkQuantity = useCallback(() => {
    const itemInBasket = basketItems.filter(basketItem => basketItem._id === item._id);
    if (itemInBasket.length > 0) {
      return itemInBasket[0].basketQuantity;
    }
    else {
      return 0;
    }
  }, [basketItems]);

  return (
    <Animatable.View
      animation="fadeInUp"
      duration={600}
      delay={index * 100}
      style={[
        styles.modernCard,
        {
          backgroundColor: theme ? 'white' : 'rgb(51,51,51)',
          margin: showCompactMode ? 8 : 12,
          minHeight: showCompactMode ? 400 : 480,
          maxWidth: Platform.select({ web: "30%", default: "98%" }),
          ...(Platform.OS === 'web' && { boxSizing: 'border-box' }),
        }
      ]}
    >
      {/* Header Section */}
      <View style={{
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: showCompactMode ? 12 : 16,
        paddingVertical: showCompactMode ? 12 : 16,
        borderBottomWidth: 1,
        borderBottomColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
      }}>
        {/* Title and Price */}
        <View style={{ flex: 1 }}>
          <XStack alignItems="center" gap={8} justifyContent="space-between">
            <H4
              numberOfLines={1}
              color={theme ? "#1e293b" : "#f1f5f9"}
              fontSize={showCompactMode ? 16 : 18}
              fontWeight="600"
              flex={1}
            >
              {item.data.product_name || item.data.event_name}
            </H4>
            <View style={{
              backgroundColor: theme ? '#f0f9ff' : '#0c4a6e',
              paddingHorizontal: 12,
              paddingVertical: 6,
              borderRadius: 8,
            }}>
              <SizableText
                fontSize={showCompactMode ? 16 : 18}
                fontWeight="700"
                color={theme ? '#0ea5e9' : '#38bdf8'}
              >
                £{parseFloat(item.data.price || item.data.RRP || item.data.ticket_price || 0).toFixed(2)}
              </SizableText>
            </View>
          </XStack>

          {/* Rating Stars */}
          <XStack alignItems="center" gap={4} marginTop={8}>
            {[1, 2, 3, 4, 5].map((star) => (
              <FontAwesome
                key={star}
                name="star"
                size={14}
                color={theme ? '#fbbf24' : '#f59e0b'}
              />
            ))}
          </XStack>
        </View>
      </View>

      {/* Content Section */}
      <View style={{ flex: 1, padding: showCompactMode ? 12 : 16 }}>
        {/* Image Carousel */}
        <View style={{
          marginBottom: 12,
          borderRadius: 12,
          overflow: 'hidden',
          backgroundColor: theme ? '#f8fafc' : '#1e293b',
        }}>
          <ErrorBoundary>
            <HorizontalCarousel
              autoPlay={false}
              imageStyle={{ maxHeight: Platform.select({ web: undefined, default: 185 }) }}
              images={item.dataImages}
              height={Platform.OS === 'web' ? 150 : 200}
            />
          </ErrorBoundary>
        </View>

        {/* Description */}
        <View style={{ marginTop: 8 }}>
          <Paragraph
            numberOfLines={3}
            fontSize={showCompactMode ? 13 : 14}
            color={theme ? "#64748b" : "#94a3b8"}
            lineHeight={20}
          >
            {getDescription()}
          </Paragraph>
        </View>
      </View>

      {/* Action Buttons - Quantity Controls */}
      <View style={{
        flexDirection: 'row',
        padding: showCompactMode ? 12 : 16,
        gap: 12,
        borderTopWidth: 1,
        borderTopColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Pressable
          onPress={_removeItemFromBasket}
          style={{
            width: 40,
            height: 40,
            borderRadius: 8,
            backgroundColor: theme ? '#fee2e2' : '#7f1d1d',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Entypo
            name="squared-minus"
            size={20}
            color={theme ? '#ef4444' : '#fca5a5'}
          />
        </Pressable>

        <View style={{
          minWidth: 50,
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <SizableText
            fontSize={20}
            fontWeight="700"
            color={theme ? "#1e293b" : "#f1f5f9"}
          >
            {checkQuantity()}
          </SizableText>
        </View>

        <Pressable
          onPress={() => _handleAddBasket(item)}
          style={{
            width: 40,
            height: 40,
            borderRadius: 8,
            backgroundColor: theme ? '#d1fae5' : '#064e3b',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Entypo
            name="squared-plus"
            size={20}
            color={theme ? '#059669' : '#10b981'}
          />
        </Pressable>
      </View>
    </Animatable.View>
  )
}

export default ProductItem;